import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, RefreshCw, Search, Store } from "lucide-react";
import AdminPageShell from "../components/AdminPageShell";
import ADMIN_PATHS from "../ADMIN_PATHS";
import {
  Badge,
  Card,
  FieldBase,
  SecondaryButton,
  SelectBase,
} from "../B2CManagement/b2cUi";
import { partnerApiRequest, resolvePartnerFileUrl } from "../../../util/API";

const normalizeText = (value) => String(value ?? "").trim();

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString();
};

const statusTone = (status) => {
  const key = normalizeText(status).toLowerCase();
  if (key === "approved") return "green";
  if (key === "rejected") return "rose";
  if (key === "under_review") return "yellow";
  if (key === "resubmitted") return "blue";
  return "gray";
};

const displayStatus = (status) => {
  const key = normalizeText(status).toLowerCase();
  if (!key) return "Unknown";
  if (key === "under_review") return "Under Review";
  if (key === "resubmitted") return "Resubmitted";
  return key.replace(/_/g, " ").replace(/\b\w/g, (ch) => ch.toUpperCase());
};

const LabelValue = ({ label, value }) => (
  <div>
    <div className="text-[11px] font-black uppercase tracking-[0.18em] text-slate-400">{label}</div>
    <div className="mt-1 text-sm text-slate-800">{normalizeText(value) || "-"}</div>
  </div>
);

const PartnerList = () => {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("approved");
  const [state, setState] = useState({
    loading: false,
    error: "",
    items: [],
    meta: { page: 1, limit: 50, total: 0 },
  });
  const [details, setDetails] = useState({
    open: false,
    loading: false,
    error: "",
    item: null,
  });

  const stats = useMemo(() => {
    const list = Array.isArray(state.items) ? state.items : [];
    const approved = list.filter((item) => normalizeText(item?.status).toLowerCase() === "approved").length;
    const underReview = list.filter((item) => normalizeText(item?.status).toLowerCase() === "under_review").length;
    const rejected = list.filter((item) => normalizeText(item?.status).toLowerCase() === "rejected").length;
    return { loaded: list.length, approved, underReview, rejected };
  }, [state.items]);

  const fetchList = async (opts = {}) => {
    const nextPage = Number(opts.page || state.meta.page || 1);
    const limit = Number(opts.limit || state.meta.limit || 50);

    setState((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      const res = await partnerApiRequest({
        method: "get",
        path: "/partner-applications",
        params: {
          page: nextPage,
          limit,
          ...(normalizeText(q) ? { q: normalizeText(q) } : {}),
          ...(normalizeText(status) ? { status: normalizeText(status) } : {}),
        },
      });

      const payload = res?.data || {};
      const meta = payload?.meta || {};
      setState({
        loading: false,
        error: "",
        items: Array.isArray(payload?.data) ? payload.data : [],
        meta: {
          page: Number(meta.page || nextPage),
          limit: Number(meta.limit || limit),
          total: Number(meta.total || 0),
        },
      });
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error:
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load partner list",
      }));
    }
  };

  const openDetails = async (id) => {
    const appId = normalizeText(id);
    if (!appId) return;

    setDetails({ open: true, loading: true, error: "", item: null });
    try {
      const res = await partnerApiRequest({
        method: "get",
        path: `/partner-applications/${encodeURIComponent(appId)}`,
      });
      setDetails({
        open: true,
        loading: false,
        error: "",
        item: res?.data?.data || null,
      });
    } catch (error) {
      setDetails({
        open: true,
        loading: false,
        error:
          error?.response?.data?.error?.message ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to load partner details",
        item: null,
      });
    }
  };

  useEffect(() => {
    fetchList({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <AdminPageShell
      title="Partner List"
      description="View registered partner accounts and store information."
      chips={
        <div className="flex flex-wrap items-center gap-2">
          <Badge tone="gray">{stats.loaded} loaded</Badge>
          <Badge tone="green">{stats.approved} approved</Badge>
          <Badge tone="yellow">{stats.underReview} under review</Badge>
          <Badge tone="rose">{stats.rejected} rejected</Badge>
        </div>
      }
      actions={
        <div className="flex items-center gap-2">
          <SecondaryButton
            type="button"
            onClick={() => navigate(ADMIN_PATHS.PARTNERAPPLICATIONMANAGEMENT)}
          >
            Open Applications
          </SecondaryButton>
          <SecondaryButton
            type="button"
            onClick={() => fetchList({ page: 1 })}
            disabled={state.loading}
          >
            <RefreshCw className="h-4 w-4" />
            {state.loading ? "Loading..." : "Refresh"}
          </SecondaryButton>
        </div>
      }
    >
      <div className="space-y-6">
        <Card className="p-5">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            <div className="lg:col-span-2">
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                Search
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <input
                  type="text"
                  className={`${FieldBase} pl-10`}
                  value={q}
                  onChange={(event) => setQ(event.target.value)}
                  placeholder="Store name, email, applicant, tracking id"
                  onKeyDown={(event) => {
                    if (event.key === "Enter") fetchList({ page: 1 });
                  }}
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-black uppercase tracking-[0.18em] text-slate-500 mb-2">
                Status
              </label>
              <select
                className={SelectBase}
                value={status}
                onChange={(event) => setStatus(event.target.value)}
              >
                <option value="">All</option>
                <option value="approved">approved</option>
                <option value="under_review">under_review</option>
                <option value="submitted">submitted</option>
                <option value="resubmitted">resubmitted</option>
                <option value="rejected">rejected</option>
              </select>
            </div>
          </div>

          {state.error ? (
            <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm font-semibold text-rose-700">
              {state.error}
            </div>
          ) : null}
        </Card>

        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">
                    Store
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">
                    Contact
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">
                    Category
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">
                    Status
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">
                    Created
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-black uppercase tracking-[0.18em] text-slate-500">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {state.items.length ? (
                  state.items.map((item) => {
                    const id = String(item?._id || item?.id || "");
                    const logoUrl = resolvePartnerFileUrl(item?.storeLogoFile?.fileUrl);
                    return (
                      <tr key={id} className="border-b border-slate-200 last:border-b-0 hover:bg-slate-50/70">
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-2xl border border-slate-200 bg-slate-50 overflow-hidden flex items-center justify-center">
                              {logoUrl ? (
                                <img src={logoUrl} alt="Store" className="h-10 w-10 object-cover" />
                              ) : (
                                <Store className="h-4 w-4 text-slate-400" />
                              )}
                            </div>
                            <div>
                              <div className="font-semibold text-slate-900">{normalizeText(item?.storeName) || "-"}</div>
                              <div className="text-xs font-mono text-slate-500">{normalizeText(item?.applicationNumber) || "-"}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-slate-900">{normalizeText(item?.applicantFullName) || "-"}</div>
                          <div className="text-xs text-slate-500">{normalizeText(item?.emailAddress) || "-"}</div>
                          <div className="text-xs text-slate-500">{normalizeText(item?.mobileNumber) || "-"}</div>
                        </td>
                        <td className="px-4 py-4 text-slate-700">{normalizeText(item?.storeCategory) || "-"}</td>
                        <td className="px-4 py-4">
                          <Badge tone={statusTone(item?.status)}>{displayStatus(item?.status)}</Badge>
                        </td>
                        <td className="px-4 py-4 text-slate-700">{formatDate(item?.createdAt)}</td>
                        <td className="px-4 py-4">
                          <div className="flex justify-end">
                            <SecondaryButton type="button" onClick={() => openDetails(id)}>
                              <Eye className="h-4 w-4" />
                              View
                            </SecondaryButton>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-500">
                      {state.loading ? "Loading..." : "No partners found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-slate-200 bg-white">
            <div className="text-xs font-semibold text-slate-600">
              Page {state.meta.page} | Showing {state.items.length} | Total {state.meta.total}
            </div>
            <div className="flex gap-2">
              <SecondaryButton
                type="button"
                onClick={() => fetchList({ page: Math.max(1, state.meta.page - 1) })}
                disabled={state.loading || state.meta.page <= 1}
              >
                Prev
              </SecondaryButton>
              <SecondaryButton
                type="button"
                onClick={() => fetchList({ page: state.meta.page + 1 })}
                disabled={state.loading || state.items.length < state.meta.limit}
              >
                Next
              </SecondaryButton>
            </div>
          </div>
        </Card>
      </div>

      {details.open ? (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-6">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={() => setDetails({ open: false, loading: false, error: "", item: null })}
          />

          <div
            className="relative w-full max-w-3xl max-h-[90vh] bg-white shadow-2xl border border-slate-200 rounded-2xl overflow-hidden flex flex-col"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">Partner</div>
                <div className="mt-1 text-lg font-black text-slate-900">Profile Details</div>
              </div>
              <SecondaryButton
                type="button"
                onClick={() => setDetails({ open: false, loading: false, error: "", item: null })}
              >
                Close
              </SecondaryButton>
            </div>

            <div className="p-5 overflow-auto flex-1">
              {details.loading ? <div className="text-sm text-slate-600">Loading...</div> : null}
              {details.error ? (
                <div className="rounded-2xl border border-rose-200 bg-rose-50 p-3 text-sm font-semibold text-rose-700">
                  {details.error}
                </div>
              ) : null}
              {details.item ? (
                <div className="space-y-5">
                  <Card className="p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <LabelValue label="Store Name" value={details.item.storeName} />
                      <LabelValue label="Status" value={displayStatus(details.item.status)} />
                      <LabelValue label="Tracking ID" value={details.item.applicationNumber} />
                      <LabelValue label="Category" value={details.item.storeCategory} />
                    </div>
                  </Card>

                  <Card className="p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <LabelValue label="Applicant" value={details.item.applicantFullName} />
                      <LabelValue label="Email" value={details.item.emailAddress} />
                      <LabelValue label="Mobile" value={details.item.mobileNumber} />
                      <LabelValue label="Designation" value={details.item.designation} />
                    </div>
                  </Card>

                  <Card className="p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <LabelValue label="Business Type" value={details.item.businessType} />
                      <LabelValue label="Legal Name" value={details.item.legalBusinessName} />
                      <LabelValue label="Country" value={details.item.registeredCountry} />
                      <LabelValue label="District" value={details.item.registeredDistrict} />
                      <LabelValue label="City" value={details.item.registeredCityMunicipality} />
                      <LabelValue label="BIN/TIN" value={details.item.binTin} />
                    </div>
                  </Card>

                  <Card className="p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <LabelValue label="Website / Social" value={details.item.websiteOrSocial} />
                      <LabelValue label="Pickup Address" value={details.item.deliveryPickupAddress} />
                      <LabelValue label="Admin Note" value={details.item.adminNote} />
                      <LabelValue label="Rejection Note" value={details.item.rejectionNote} />
                      <LabelValue label="Created" value={formatDate(details.item.createdAt)} />
                      <LabelValue label="Updated" value={formatDate(details.item.updatedAt)} />
                    </div>
                  </Card>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </AdminPageShell>
  );
};

export default PartnerList;
